// src/pages/Performance.jsx

import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, query, where, onSnapshot, addDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { FaChartLine, FaPlus, FaTrophy, FaSave } from "react-icons/fa";
import { FiBarChart2, FiCalendar, FiHash, FiTarget, FiTrendingUp } from "react-icons/fi";
import { fmtINR } from "../utils/salaryUtils";
import { notifyUser } from "../utils/notifications";
import "./ManagerComponent.css";
import "./Performance.css";

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-IN", { timeZone: 'Asia/Kolkata', day: "2-digit", month: "short", year: "numeric" });
};

const Performance = ({ selectedEmployee }) => {
  const { userData } = useAuth();
  const isManagementView = !!selectedEmployee;
  const userInFocus = isManagementView ? selectedEmployee : userData;
  // Admin can manage only employees; superadmin can manage admins + employees
  const canManage =
    userData.role === "superadmin" ||
    (userData.role === "admin" && isManagementView && userInFocus.role === "employee");

  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMetric, setNewMetric] = useState({ metric: "", value: "", recordedAt: new Date().toISOString().split("T")[0] });

  // ── Performance KPI state ──────────────────────────────────────────────────
  const [kpiValue, setKpiValue]         = useState("");
  const [savedKpi, setSavedKpi]         = useState(null);
  const [kpiLoading, setKpiLoading]     = useState(false);
  const [kpiSaving, setKpiSaving]       = useState(false);
  const [ctcBreakdown, setCtcBreakdown] = useState(null);

  const fetchKpi = async () => {
    if (!userInFocus?.uid) return;
    setKpiLoading(true);
    try {
      const userSnap = await getDoc(doc(db, "users", userInFocus.uid));
      if (userSnap.exists()) {
        const kpi = userSnap.data().performance_kpi ?? 0;
        setSavedKpi(kpi);
        setKpiValue(String(Math.round(kpi * 100)));
      }
    } catch (e) { console.warn("fetchKpi:", e); }
    setKpiLoading(false);
  };

  const fetchCtcBreakdown = async () => {
    if (!userInFocus?.employeeId) return;
    try {
      const snap = await getDoc(doc(db, "employee_records_complete", userInFocus.employeeId));
      if (snap.exists()) {
        const variablePayPool = Number(snap.data().variableComponents?.variablePayPool) || 0;
        setCtcBreakdown(variablePayPool > 0 ? { variablePool: variablePayPool, hasVariable: true } : null);
      }
    } catch (e) { console.warn("fetchCtcBreakdown:", e); }
  };

  const handleSaveKpi = async () => {
    const pct = parseFloat(kpiValue);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      alert("KPI must be between 0 and 100.");
      return;
    }
    const kpiFloat = Math.round(pct) / 100;
    setKpiSaving(true);
    try {
      await updateDoc(doc(db, "users", userInFocus.uid), { performance_kpi: kpiFloat });
      setSavedKpi(kpiFloat);
      alert(`✅ Performance KPI updated to ${Math.round(pct)}% for ${userInFocus.name}.`);
      // Notify the employee that their KPI was updated
      if (userInFocus.uid !== userData?.uid) {
        notifyUser({
          recipientUid: userInFocus.uid,
          type: 'performance_kpi',
          title: 'Performance KPI Updated',
          body: `Your performance KPI has been set to ${Math.round(pct)}% by ${userData?.name || 'admin'}.`,
          link: '/dashboard',
          senderUid: userData?.uid,
          senderName: userData?.name || '',
          metadata: { kpi: kpiFloat },
        });
      }
    } catch (e) {
      alert("Failed to update KPI. Please try again.");
      console.error("saveKpi:", e);
    }
    setKpiSaving(false);
  };

  useEffect(() => {
    if (!userInFocus?.uid) return;
    setLoading(true);
    const q = query(collection(db, "performance"), where("uid", "==", userInFocus.uid));
    const unsub = onSnapshot(q, (snap) => {
      setMetrics(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => new Date(b.recordedAt) - new Date(a.recordedAt)));
      setLoading(false);
    }, (err) => { console.error(err); setLoading(false); });
    return () => unsub();
  }, [userInFocus?.uid]);

  const addMetric = async () => {
    if (!newMetric.metric || !newMetric.value) return alert("Metric and Value are required.");
    try {
      await addDoc(collection(db, "performance"), { ...newMetric, uid: userInFocus.uid });
      setNewMetric({ metric: "", value: "", recordedAt: new Date().toISOString().split("T")[0] });
      alert("Metric added!");
      // Notify the employee that a performance metric was added
      if (userInFocus.uid !== userData?.uid) {
        notifyUser({
          recipientUid: userInFocus.uid,
          type: 'performance_metric',
          title: 'New Performance Metric Added',
          body: `${userData?.name || 'Admin'} added a performance record: ${newMetric.metric} — ${newMetric.value}.`,
          link: '/dashboard',
          senderUid: userData?.uid,
          senderName: userData?.name || '',
          metadata: { metric: newMetric.metric },
        });
      }
    } catch (error) { alert("Failed to add metric."); }
  };

  useEffect(() => {
    if (userInFocus?.uid) {
      fetchKpi();
      if (isManagementView) fetchCtcBreakdown();
    }
  }, [userInFocus]);

  // Compute estimated release from KPI + CTC
  const kpiFloat   = parseFloat(kpiValue) / 100 || 0;
  const varPool    = ctcBreakdown?.variablePool || 0;
  const kpiRelease = varPool * kpiFloat;

  return (
    <div className="perf-shell">

      {/* ══ Page Header ══════════════════════════════════════════════ */}
      <div className="perf-page-header">
        <div className="perf-page-header-left">
          <span className="perf-header-icon-wrap"><FiTrendingUp size={20} /></span>
          <div>
            <h3 className="perf-page-title">
              Performance Metrics
              {isManagementView && <span className="perf-page-for"> — {userInFocus.name}</span>}
            </h3>
            <p className="perf-page-subtitle">Quarterly KPIs &amp; performance records</p>
          </div>
        </div>
        {metrics.length > 0 && (
          <span className="perf-metrics-badge">{metrics.length} record{metrics.length !== 1 ? "s" : ""}</span>
        )}
      </div>

      {/* ══ KPI Panel (admin/superadmin in management view) ════════════ */}
      {canManage && isManagementView && (
        <div className="perf-kpi-card">
          <div className="perf-kpi-card-header">
            <span className="perf-kpi-icon-wrap"><FaTrophy size={18} /></span>
            <div>
              <h4 className="perf-kpi-title">Variable Pay – Performance KPI</h4>
              <p className="perf-kpi-subtitle">Admin / Superadmin only</p>
            </div>
          </div>

          <p className="perf-kpi-desc">
            Set the <strong>Performance KPI %</strong> for this employee. Variable Pay released at end of
            financial year = <strong>Variable Pay Pool × KPI%</strong>. Default is 0% on joining.
          </p>

          {/* CTC info row */}
          {ctcBreakdown?.hasVariable ? (
            <div className="perf-kpi-info-row perf-kpi-info--amber">
              <span><strong>CTC Variable Pool:</strong> ₹{fmtINR(varPool)}/yr</span>
              {savedKpi !== null && (
                <>
                  <span className="perf-kpi-divider">|</span>
                  <span><strong>Current KPI:</strong> {Math.round(savedKpi * 100)}%</span>
                  <span className="perf-kpi-divider">|</span>
                  <span><strong>Est. Release:</strong> ₹{fmtINR(varPool * savedKpi)}/yr</span>
                </>
              )}
            </div>
          ) : (
            <div className="perf-kpi-info-row perf-kpi-info--neutral">
              No Variable Pay Pool set for this employee — set it via Employee Detail → Edit Salary Breakdown.
            </div>
          )}

          {/* KPI controls */}
          {kpiLoading ? (
            <p className="perf-loading-text">Loading KPI…</p>
          ) : (
            <div className="perf-kpi-controls">
              {/* Circular gauge */}
              <div className="perf-gauge-wrap">
                <svg className="perf-gauge-svg" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="32" className="perf-gauge-track" />
                  <circle
                    cx="40" cy="40" r="32"
                    className="perf-gauge-fill"
                    style={{
                      strokeDasharray: `${2 * Math.PI * 32}`,
                      strokeDashoffset: `${2 * Math.PI * 32 * (1 - (parseFloat(kpiValue) || 0) / 100)}`,
                    }}
                  />
                </svg>
                <span className="perf-gauge-label">{parseFloat(kpiValue) || 0}%</span>
              </div>

              <div className="perf-kpi-inputs">
                <label className="perf-kpi-label">KPI %</label>
                <div className="perf-kpi-input-row">
                  <input
                    type="number" min="0" max="100" step="1"
                    placeholder="0–100" value={kpiValue}
                    onChange={e => setKpiValue(e.target.value)}
                    className="perf-kpi-number-input"
                  />
                  <input
                    type="range" min="0" max="100" step="5"
                    value={parseFloat(kpiValue) || 0}
                    onChange={e => setKpiValue(e.target.value)}
                    className="perf-kpi-range"
                  />
                </div>
                {ctcBreakdown?.hasVariable && kpiValue !== "" && (
                  <p className="perf-kpi-release-hint">
                    → Estimated release: <strong>₹{fmtINR(kpiRelease)}</strong>/yr
                  </p>
                )}
                <button
                  onClick={handleSaveKpi}
                  disabled={kpiSaving}
                  className="perf-save-btn"
                >
                  <FaSave size={13} /> {kpiSaving ? "Saving…" : "Update KPI"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ Add Metric Form (admin/superadmin in management view) ══════ */}
      {isManagementView && canManage && (
        <div className="perf-add-form">
          <div className="perf-add-form-header">
            <FiBarChart2 size={16} />
            <h4>Add New Metric</h4>
          </div>
          <div className="perf-add-form-grid">
            <div className="perf-field">
              <label className="perf-field-label">Metric Name</label>
              <input
                className="perf-input"
                placeholder="e.g., Task Completion Rate"
                value={newMetric.metric}
                onChange={e => setNewMetric({ ...newMetric, metric: e.target.value })}
              />
            </div>
            <div className="perf-field">
              <label className="perf-field-label">Value</label>
              <input
                className="perf-input"
                placeholder="e.g., 95% or 4.5/5"
                value={newMetric.value}
                onChange={e => setNewMetric({ ...newMetric, value: e.target.value })}
              />
            </div>
            <div className="perf-field">
              <label className="perf-field-label">Record Date</label>
              <input
                className="perf-input"
                type="date"
                value={newMetric.recordedAt}
                onChange={e => setNewMetric({ ...newMetric, recordedAt: e.target.value })}
              />
            </div>
            <div className="perf-field perf-field--action">
              <button onClick={addMetric} className="perf-add-btn">
                <FaPlus size={12} /> Add Metric
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Metrics Table ══════════════════════════════════════════════ */}
      {loading ? (
        <div className="perf-loading-state">
          <FiBarChart2 size={28} />
          <p>Loading metrics…</p>
        </div>
      ) : metrics.length === 0 ? (
        <div className="perf-empty-state">
          <FiTarget size={40} />
          <h4>No metrics recorded yet</h4>
          <p>
            {canManage && isManagementView
              ? "Use the form above to add the first performance metric."
              : "Your manager hasn't recorded any performance metrics yet."}
          </p>
        </div>
      ) : (
        <div className="perf-table-card">
          <div className="perf-table-card-header">
            <FiBarChart2 size={15} />
            <span>Performance Records</span>
            <span className="perf-table-count">{metrics.length}</span>
          </div>
          <div className="perf-table-wrapper">
            <table className="perf-table">
              <thead>
                <tr>
                  <th className="perf-th perf-th--num"><FiHash size={12} /> #</th>
                  <th className="perf-th"><FiBarChart2 size={12} /> Metric</th>
                  <th className="perf-th"><FiTarget size={12} /> Value</th>
                  <th className="perf-th"><FiCalendar size={12} /> Recorded On</th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((m, idx) => (
                  <tr key={m.id} className="perf-tr">
                    <td className="perf-td perf-td--num">{idx + 1}</td>
                    <td className="perf-td perf-td--metric">{m.metric}</td>
                    <td className="perf-td">
                      <span className="perf-value-chip">{m.value}</span>
                    </td>
                    <td className="perf-td perf-td--date">
                      <span className="perf-date-pill">
                        <FiCalendar size={11} />
                        {formatDateDisplay(m.recordedAt)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Performance;
